import { ModelSetTransformer, ModelTransformer } from '../common/common.model';
import { Util } from '../common/common.util';

import { determineJournalUrl } from './journal-type.widget.model';

export interface ExtCaoOpportunityPopupCoaching {
    managerID: number;
    userID: number;
    metricID: number;
    coachingProcessInstanceId: number;
    diagnosticStartDate: string;
    followUpEndDate: string;
    wasGoalMet: number;
    wasFollowUpGoalCreated: number;
    diagObs1Date: string;
    diagObs1JournalID: number;
    diagObs1JournalType: number;
    diag1Counts: number;
    diagObs2Date: string;
    diagObs2JournalID: number;
    diagObs2JournalType: number;
    diag2Counts: number;
    primaryGoalStartDate: string;
    primaryGoalID: number;
    journalTypeID: number;
    goalCreatedCounts: number;
    followUp1Date: string;
    followUp1JournalID: number;
    followUp1JournalType: number;
    followUp1Counts: number;
    followUp2Date: string;
    followUp2JournalID: number;
    followUp2JournalType: number;
    followUp2Counts: number;
    completionDate: string;
    completionJounalID: number;
    completionJournalType: number;
    completionCounts: number;
}

export interface ExtCaoOpportunityPopupMetric {
    managerID: number;
    userID: number;
    metricID: number;
    metricName: string;
    currentActual: number;
    currentThreshold: number;
    diag1Met: number;
    diag2Met: number;
    goalStartedMet: number;
    followUp1Met: number;
    followUp2Met: number;
    completionActionMet: number;
    caoCoachings: ExtCaoOpportunityPopupCoaching[];
}

export interface ExtCaoOpportunityPopupUser {
    managerID: number;
    userID: number;
    individual: string;
    percentOfPeriod: number;
    caoEarnedPoints: number;
    caoAvailablePoints: number;
    cpcEarnedPoints: number;
    cpcAvailablePoints: number;
    currentOps: number;
    expectedOps: number;
    oppsInProgress: number;
    diag1Met: number;
    diag2Met: number;
    goalStartedMet: number;
    followUp1Met: number;
    followUp2Met: number;
    completionActionMet: number;
    metrics: ExtCaoOpportunityPopupMetric[];
}

export interface ExtCaoOpportunityPopup {
    managerID: number;
    caoEarnedPoints: number;
    caoAvailablePoints: number;
    cpcEarnedPoints: number;
    cpcAvailablePoints: number;
    currentOps: number;
    expectedOps: number;
    oppsInProgress: number;
    diag1Performed: number;
    diag2Performed: number;
    goalStartedPerformed: number;
    followUp1Performed: number;
    followUp2Performed: number;
    completionActionPerformed: number;
    caoUsers: ExtCaoOpportunityPopupUser[];
}

export interface CaoOpportunityPopupCoaching {
    diagnosticStartDate: Date;
    followUpEndDate: Date;
    wasGoalMet: boolean;
    wasFollowUpGoalCreated: boolean;
    diagObs1Date: Date;
    diagObs1JournalUrl: string;
    primaryGoalStartDate: Date;
    primaryGoalJournalUrl: string;
    followUp1Date: Date;
    followUp1JournalUrl: string;
    followUp2Date: Date;
    followUp2JournalUrl: string;
    completionDate: Date;
    completionJournalUrl: string;
}

export interface CaoOpportunityPopupMetric {
    id: number;
    name: string;
    currentActual: number;
    diag1Met: boolean;
    goalStartedMet: boolean;
    followUp1Met: boolean;
    followUp2Met: boolean;
    completionActionMet: boolean;
    coaching: CaoOpportunityPopupCoaching[];
    userId: number;
}

export interface CaoOpportunityPopupUser {
    userId: number;
    username: string;
    caoPercent: number;
    cpcPercent: number;
    currentOps: number;
    opsPercent: number;
    diag1Met: boolean;
    goalStartedMet: boolean;
    followUp1Met: boolean;
    followUp2Met: boolean;
    completionActionMet: boolean;
    metrics: CaoOpportunityPopupMetric[];
}

export interface CaoOpportunityPopupOverAll {
    caoPercent: number;
    cpcPercent: number;
    currentOps: number;
    opsPercent: number;
    diag1Performed: number;
    diag1Percent: number;
    goalStartedPerformed: number;
    goalStartedPercent: number;
    followUp1Performed: number;
    followUp1Percent: number;
    followUp2Performed: number;
    followUp2Percent: number;
    completionActionPerformed: number;
    completionActionPercent: number;
}

export interface CaoOpportunityPopup {
    managerId: number;
    overall: CaoOpportunityPopupOverAll;
    items: CaoOpportunityPopupUser[];
}

class CaoOpportunityPopupCoachingModelTransformer
    implements
        ModelTransformer<
            CaoOpportunityPopupCoaching,
            ExtCaoOpportunityPopupCoaching
        >
{
    transform(
        model: ExtCaoOpportunityPopupCoaching,
    ): CaoOpportunityPopupCoaching {
        return {
            diagnosticStartDate: Util.dateToDate(model.diagnosticStartDate),
            followUpEndDate: Util.dateToDate(model.followUpEndDate),
            wasGoalMet: model.wasGoalMet != null && model.wasGoalMet !== 0,
            wasFollowUpGoalCreated:
                model.wasFollowUpGoalCreated != null &&
                model.wasFollowUpGoalCreated !== 0,
            diagObs1Date: Util.dateToDate(model.diagObs1Date),
            diagObs1JournalUrl: determineJournalUrl(
                model.diagObs1JournalType,
                model.diagObs1JournalID,
            ),
            primaryGoalStartDate: Util.dateToDate(model.primaryGoalStartDate),
            primaryGoalJournalUrl: determineJournalUrl(
                model.journalTypeID,
                model.primaryGoalID,
            ),
            followUp1Date: Util.dateToDate(model.followUp1Date),
            followUp1JournalUrl: determineJournalUrl(
                model.followUp1JournalType,
                model.followUp1JournalID,
            ),
            followUp2Date: Util.dateToDate(model.followUp2Date),
            followUp2JournalUrl: determineJournalUrl(
                model.followUp2JournalType,
                model.followUp2JournalID,
            ),
            completionDate: Util.dateToDate(model.completionDate),
            completionJournalUrl: determineJournalUrl(
                model.completionJournalType,
                model.completionJounalID,
            ),
        };
    }
}

class CaoOpportunityPopupCoachingModelSetTransformer extends ModelSetTransformer<
    CaoOpportunityPopupCoaching,
    ExtCaoOpportunityPopupCoaching
> {
    protected newTransformer(): ModelTransformer<
        CaoOpportunityPopupCoaching,
        ExtCaoOpportunityPopupCoaching
    > {
        return new CaoOpportunityPopupCoachingModelTransformer();
    }
}

class CaoOpportunityPopupMetricModelTransformer
    implements
        ModelTransformer<
            CaoOpportunityPopupMetric,
            ExtCaoOpportunityPopupMetric
        >
{
    transform(model: ExtCaoOpportunityPopupMetric): CaoOpportunityPopupMetric {
        const coaching =
            new CaoOpportunityPopupCoachingModelSetTransformer().transform(
                model.caoCoachings,
            );
        return {
            id: model.metricID,
            name: model.metricName,
            currentActual: model.currentActual,
            diag1Met: model.diag1Met != null && model.diag1Met !== 0,
            goalStartedMet:
                model.goalStartedMet != null && model.goalStartedMet !== 0,
            followUp1Met:
                model.followUp1Met != null && model.followUp1Met !== 0,
            followUp2Met:
                model.followUp2Met != null && model.followUp2Met !== 0,
            completionActionMet:
                model.completionActionMet != null &&
                model.completionActionMet !== 0,
            coaching,
            userId: model.userID,
        };
    }
}

class CaoOpportunityPopupMetricModelSetTransformer extends ModelSetTransformer<
    CaoOpportunityPopupMetric,
    ExtCaoOpportunityPopupMetric
> {
    protected newTransformer(): ModelTransformer<
        CaoOpportunityPopupMetric,
        ExtCaoOpportunityPopupMetric
    > {
        return new CaoOpportunityPopupMetricModelTransformer();
    }
}

class CaoOpportunityPopupUserModelTransformer
    implements
        ModelTransformer<CaoOpportunityPopupUser, ExtCaoOpportunityPopupUser>
{
    transform(model: ExtCaoOpportunityPopupUser): CaoOpportunityPopupUser {
        const metrics =
            new CaoOpportunityPopupMetricModelSetTransformer().transform(
                model.metrics,
            );
        return {
            userId: model.userID,
            username: model.individual,
            caoPercent:
                model.caoAvailablePoints <= 0
                    ? 100
                    : (model.caoEarnedPoints * 100) / model.caoAvailablePoints,
            cpcPercent:
                model.cpcAvailablePoints <= 0
                    ? 100
                    : (model.cpcEarnedPoints * 100) / model.cpcAvailablePoints,
            currentOps: model.currentOps,
            opsPercent:
                model.expectedOps <= 0
                    ? 100
                    : (model.oppsInProgress * 100) / model.expectedOps,
            diag1Met: model.diag1Met != null && model.diag1Met !== 0,
            goalStartedMet:
                model.goalStartedMet != null && model.goalStartedMet !== 0,
            followUp1Met:
                model.followUp1Met != null && model.followUp1Met !== 0,
            followUp2Met:
                model.followUp2Met != null && model.followUp2Met !== 0,
            completionActionMet:
                model.completionActionMet != null &&
                model.completionActionMet !== 0,
            metrics,
        };
    }
}

class CaoOpportunityPopupOverAllModelTransformer
    implements
        ModelTransformer<CaoOpportunityPopupOverAll, ExtCaoOpportunityPopup>
{
    transform(model: ExtCaoOpportunityPopup): CaoOpportunityPopupOverAll {
        return {
            caoPercent:
                model.caoAvailablePoints <= 0
                    ? 100
                    : (model.caoEarnedPoints * 100) / model.caoAvailablePoints,
            cpcPercent:
                model.cpcAvailablePoints <= 0
                    ? 100
                    : (model.cpcEarnedPoints * 100) / model.cpcAvailablePoints,
            currentOps: model.currentOps,
            opsPercent:
                model.expectedOps <= 0
                    ? 100
                    : (model.oppsInProgress * 100) / model.expectedOps,
            diag1Performed: model.diag1Performed,
            diag1Percent:
                model.expectedOps <= 0
                    ? 0
                    : (100 / model.expectedOps) * model.diag1Performed,
            goalStartedPerformed: model.goalStartedPerformed,
            goalStartedPercent:
                model.expectedOps <= 0
                    ? 0
                    : (100 / model.expectedOps) * model.goalStartedPerformed,
            followUp1Performed: model.followUp1Performed,
            followUp1Percent:
                model.expectedOps <= 0
                    ? 0
                    : (100 / model.expectedOps) * model.followUp1Performed,
            followUp2Performed: model.followUp2Performed,
            followUp2Percent:
                model.expectedOps <= 0
                    ? 0
                    : (100 / model.expectedOps) * model.followUp2Performed,
            completionActionPerformed: model.completionActionPerformed,
            completionActionPercent:
                model.expectedOps <= 0
                    ? 0
                    : (100 / model.expectedOps) *
                      model.completionActionPerformed,
        };
    }
}

class CaoOpportunityPopupUserModelSetTransformer extends ModelSetTransformer<
    CaoOpportunityPopupUser,
    ExtCaoOpportunityPopupUser
> {
    protected newTransformer(): ModelTransformer<
        CaoOpportunityPopupUser,
        ExtCaoOpportunityPopupUser
    > {
        return new CaoOpportunityPopupUserModelTransformer();
    }
}

export class CaoOpportunityPopupModelTransformer
    implements ModelTransformer<CaoOpportunityPopup, ExtCaoOpportunityPopup>
{
    transform(model: ExtCaoOpportunityPopup): CaoOpportunityPopup {
        const overall =
            new CaoOpportunityPopupOverAllModelTransformer().transform(model);
        const items =
            new CaoOpportunityPopupUserModelSetTransformer().transform(
                model.caoUsers,
            );
        return {
            managerId: model.managerID,
            overall,
            items,
        };
    }
}

export function validateCaoOpportunityPopup(model: CaoOpportunityPopup): void {
    const valid =
        model != null &&
        model.managerId > 0 &&
        model.overall != null &&
        model.items != null &&
        model.items.length !== 0;
    if (!valid) {
        throw new Error('CaoOpportunityPopup not valid');
    }
}
